import { billingService } from '../../../api';
import { icons } from '../../../icons';
import { timeConverterMMDDYYHHMM } from '../../../utils/dates';
import { formatCurrency } from '../../../utils/display-price';

export type OrderActionId = 'accept' | 'cancel' | 'verify' | 'fraud' | 'pending' | 'delete' | 'fraud_check';

export type OrderAction = {
  id: OrderActionId;
  title: string;
  icon: any;
  message: string;
  successMessage: string;
  divider?: boolean;
};

export const getDisplayName = (value: any) => {
  if (!value) return 'n/a';
  if (typeof value === 'string' || typeof value === 'number') return `${value}`;
  return value.display || value.name || value.full_name || value.email || value.id || 'n/a';
};

export const getEntityId = (value: any) => {
  if (!value) return undefined;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return /^\d+$/.test(value) ? value : undefined;
  return value.id;
};

export const getOrderStatusLabel = (status: string | undefined, t: any) => {
  switch ((status || '').toLowerCase()) {
    case 'completed':
      return t('COMPLETED');
    case 'verified':
      return t('VERIFIED');
    case 'pending':
      return t('PENDING');
    case 'cancelled':
    case 'canceled':
    case 'cancel':
      return t('CANCELED');
    case 'fraud':
      return t('FRAUD');
    default:
      return status || 'n/a';
  }
};

export const formatDateTime = (value?: string | null) => value ? timeConverterMMDDYYHHMM(value) : 'n/a';

export const formatOrderTotal = (total: any, currency: any) => {
  if (total === null || total === undefined || total === '') return 'n/a';
  return currency ? formatCurrency(total, currency) : `${total}`;
};

export const getOrderActions = (order: any, t: any): OrderAction[] => {
  const status = `${order?.status || ''}`.toLowerCase();
  const orderName = order?.id || '';
  const actions: OrderAction[] = [];

  if (status !== 'completed') {
    actions.push({
      id: 'accept',
      title: t('ACCEPT'),
      icon: icons.CheckMark,
      message: t('ACCEPT_ORDER_CONFIRM', { id: orderName }),
      successMessage: t('ORDER_ACCEPT_SCHEDULED'),
    });
  }

  if (status !== 'cancel' && status !== 'cancelled' && status !== 'canceled') {
    actions.push({
      id: 'cancel',
      title: t('CANCEL'),
      icon: icons.CloseIcon,
      message: t('CANCEL_ORDER_CONFIRM', { id: orderName }),
      successMessage: t('ORDER_CANCELLED'),
    });
  }

  if (status !== 'verified') {
    actions.push({
      id: 'verify',
      title: t('VERIFY'),
      icon: icons.CheckMark,
      message: t('VERIFY_ORDER_CONFIRM', { id: orderName }),
      successMessage: t('ORDER_SET_AS_VERIFIED'),
    });
  }

  if (status !== 'fraud') {
    actions.push({
      id: 'fraud',
      title: t('SET_AS_FRAUD'),
      icon: icons.Warning,
      message: t('FRAUD_ORDER_CONFIRM', { id: orderName }),
      successMessage: t('ORDER_SET_AS_FRAUD'),
    });
  }

  if (status !== 'pending') {
    actions.push({
      id: 'pending',
      title: t('SET_BACK_TO_PENDING'),
      icon: icons.BackArrowRevert,
      message: t('PENDING_ORDER_CONFIRM', { id: orderName }),
      successMessage: t('ORDER_SET_AS_PENDING'),
    });
  }

  actions.push({
    id: 'delete',
    title: t('DELETE'),
    icon: icons.DeleteIcon,
    message: t('DELETE_ORDER_CONFIRM', { id: orderName }),
    successMessage: t('ORDER_DELETED'),
    divider: true,
  });

  actions.push({
    id: 'fraud_check',
    title: t('RUN_FRAUD_CHECK'),
    icon: icons.EyeIc,
    message: t('RUN_FRAUD_CHECK_CONFIRM', { id: orderName }),
    successMessage: t('ORDER_FRAUD_CHECK_SCHEDULED'),
  });

  return actions;
};

export const executeOrderAction = (actionId: OrderActionId, orderId: string | number) => {
  switch (actionId) {
    case 'accept':
      return billingService.acceptOrder(orderId);
    case 'cancel':
      return billingService.cancelOrder(orderId);
    case 'verify':
      return billingService.verifyOrder(orderId);
    case 'fraud':
      return billingService.setOrderFraud(orderId);
    case 'pending':
      return billingService.setOrderPending(orderId);
    case 'delete':
      return billingService.deleteOrder(orderId);
    case 'fraud_check':
      return billingService.runOrderFraudCheck(orderId);
    default:
      return Promise.reject(new Error('Unknown order action'));
  }
};

export const getApiErrorMessage = (error: any, fallback: string) => {
  const data = error?.response?.data || error?.error;
  if (data?.messages?.length) {
    return `${data.detail ? `${data.detail}: ` : ''}${data.messages[0]}`;
  }
  if (data?.non_field_errors?.length) {
    return data.non_field_errors[0];
  }
  return data?.detail || error?.message || fallback;
};
